import { PropsWithChildren, useCallback, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useHint } from '../hint.context';
import { calcPosition } from '../hint.helpers';
import { ContentWrapper, Arrow } from '../hint.styled';
import { Side, Align, HintPosition } from '../hint.types';

export type ContentProps = {
  side?: Side;
  align?: Align;
  showArrow?: boolean;
};

export const Content = ({
  children,
  side = 'top',
  align = 'center',
  showArrow = false,
}: PropsWithChildren<ContentProps>) => {
  const { open, closeImmediate, triggerRef, contentId, triggerMode } =
    useHint();

  const [shouldRender, setShouldRender] = useState<boolean>(false);

  if (open && !shouldRender) {
    setShouldRender(true);
  }

  const [pos, setPos] = useState<HintPosition | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const refCallback = useCallback(
    (el: HTMLDivElement | null) => {
      contentRef.current = el;

      if (!el || !triggerRef.current) {
        return;
      }
      const rect = triggerRef.current.getBoundingClientRect();
      setPos(calcPosition(rect, el, side, align));
    },
    [side, align, triggerRef]
  );

  if (!shouldRender) {
    return null;
  }

  const actualSide = pos?.actualSide ?? side;

  return ReactDOM.createPortal(
    <>
      {triggerMode !== 'hover' && <div onMouseDown={closeImmediate} />}

      <ContentWrapper
        ref={refCallback}
        id={contentId}
        role="tooltip"
        $open={open}
        $side={side}
        style={
          pos ? { top: pos.top, left: pos.left } : { visibility: 'hidden' }
        }
        onAnimationEnd={() => {
          if (!open) {
            setShouldRender(false);
            setPos(null);
          }
        }}
      >
        {showArrow && <Arrow $side={actualSide} $offset={pos?.arrowOffset} />}
        {children}
      </ContentWrapper>
    </>,
    document.body
  );
};
